import { createClient } from '@supabase/supabase-js'
import { LISIBLE_ONBOARDING } from '../onboarding/erreurs'
import { LISIBLE_COMPTES, messageErreurFonction } from './identifiants'
import { CLE_PUBLISHABLE, URL_SUPABASE } from './projet'

/**
 * Le MÊME projet Supabase que les autres surfaces SwiftEats.
 *
 * Adresse et clé publique : `./projet.ts` (valeur par défaut = production actuelle,
 * remplaçable au build par `VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY`).
 * La clé est **publique** par conception : ce qu'un porteur peut lire ou écrire est
 * décidé uniquement par les policies RLS. Aucune clé `service_role` ne doit apparaître
 * ici — elle contournerait la RLS et se retrouverait dans le bundle JavaScript.
 */
export { CLE_PUBLISHABLE, URL_SUPABASE }

export const supabase = createClient(URL_SUPABASE, CLE_PUBLISHABLE, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    /* « Continuer avec Google » : le retour d'OAuth arrive sur la console avec
       `?code=…` (flux PKCE — aucun jeton dans l'URL). supabase-js l'échange
       contre une session au démarrage, puis nettoie l'URL ; `getSession()`
       attend cet échange. Sans cette option, la session Google n'était jamais
       créée. */
    flowType: 'pkce',
    detectSessionInUrl: true,
  },
})

/**
 * Erreur d'edge function, rendue lisible.
 *
 * supabase-js remonte « Edge Function returned a non-2xx status code » — une
 * phrase qui ne dit ni ce qui a échoué ni pourquoi, alors que la fonction a
 * répondu une raison précise dans le corps. On la lit et on la traduit.
 */
export const LISIBLE: Record<string, string> = {
  /* Codes d'erreur de l'intégration des prestataires (migration 0045) et des
     edge functions de contrat, dont `documenso_non_configure`. Posés EN PREMIER :
     les entrées ci-dessous les écrasent en cas de doublon. */
  ...LISIBLE_ONBOARDING,
  email_already_used: "Cette adresse e-mail est déjà utilisée par un autre compte.",
  invalid_email: "Adresse e-mail invalide.",
  /* Création de comptes (e-mail ou numéro WhatsApp) : l'adresse ou le numéro peut déjà appartenir à quelqu'un. */
  ...LISIBLE_COMPTES,
  deja_livreur: "Ce compte est déjà livreur.",
  deja_gerant: "Ce compte gère déjà un autre établissement.",
  forbidden: "Vous n'avez pas le droit d'effectuer cette action — elle est réservée à un administrateur ou à un membre autorisé.",
  forbidden_for_role: "Votre rôle ne permet pas de faire avancer cette commande.",
  unauthorized: "Session expirée — reconnectez-vous.",
  invalid_transition: "Cette commande n'est plus dans l'état attendu. Rechargez la page.",
  not_ready: "La commande doit d'abord être marquée « prête ».",
  no_driver: "Aucun livreur n'est encore affecté à cette commande.",
  conflict: "Quelqu'un a modifié cette commande en même temps que vous. Rechargez la page.",
  /* Les autres refus de `set-order-status` (affectation et annulation). */
  order_not_found: "Cette commande n'existe plus. Rechargez la page.",
  missing_params: "Demande incomplète. Rechargez la page et réessayez.",
  missing_driver_id: "Choisissez le livreur à affecter.",
  already_claimed: "Un livreur est déjà affecté à cette commande. Rechargez la page.",
  cancel_reason_required: "Un motif est obligatoire pour annuler une commande.",
  /* Migration 0053 : un livreur peut commander avec son compte, mais ne livre jamais sa propre commande. */
  livreur_client_de_la_commande: "Ce livreur est le client de cette commande : il ne peut pas la livrer. Choisissez un autre livreur.",
  commande_propre_etablissement: "Un membre de l'équipe d'un établissement ne peut pas y commander avec son compte.",
  bad_json: "Demande illisible. Rechargez la page et réessayez.",
  db_error: "La base a refusé l'opération. Réessayez dans un instant ; si cela persiste, prévenez l'équipe technique.",
  method_not_allowed: "Opération non permise.",
}

/** Codes dont le `detail` du serveur dit PLUS que la phrase fixe (liste des champs manquants, des marques…). */
export const DETAIL_PRIORITAIRE: ReadonlySet<string> = new Set([
  'identite_incomplete',
  'contrat_marques_inconnues',
  /* Numéro refusé : le serveur joint la phrase française exacte (pays, longueur…). */
  'invalid_phone',
  /* Le serveur distingue l'auto-affectation du choix du dispatch. */
  'livreur_client_de_la_commande',
])

/** Le corps envoyé aux edge functions est toujours un objet JSON simple. */
export type CorpsFonction = Record<string, unknown>

export async function appelerFonction<T>(nom: string, corps: CorpsFonction): Promise<T> {
  const { data, error } = await supabase.functions.invoke(nom, { body: corps })

  if (error) {
    let code = ''
    let detail = ''
    try {
      const contexte = (error as { context?: { json?: () => Promise<unknown> } }).context
      const json = (await contexte?.json?.()) as { error?: string; detail?: string } | undefined
      code = json?.error ?? ''
      detail = json?.detail ?? ''
    } catch {
      /* Le corps n'était pas du JSON : on garde le message brut plutôt que rien. */
    }
    throw new Error(messageErreurFonction(code, detail, LISIBLE, error.message || `${nom} a échoué`, DETAIL_PRIORITAIRE))
  }

  /* ⚠️ Une edge function peut répondre 200 AVEC un `error` dans le corps.
     Ne tester que `error` de supabase-js laisserait passer un refus pour un
     succès — et l'écran afficherait une commande avancée qui ne l'est pas. */
  const reponse = data as { error?: string; detail?: string } | null
  if (reponse && typeof reponse === 'object' && reponse.error) {
    throw new Error(messageErreurFonction(reponse.error, reponse.detail ?? '', LISIBLE, reponse.error, DETAIL_PRIORITAIRE))
  }
  return data as T
}
